import React, { useState, useEffect, useCallback } from 'react';
import { dealerAPI } from '../../services/dealerAPI';
import useDealerPlanPayment from '../../hooks/useDealerPlanPayment';

const fmt = (n) => `₹${Number(n).toLocaleString('en-IN')}`;

export default function ListingCreditsModal({ onClose, onSuccess }) {
  const [plans,          setPlans]          = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [selectedPlan,   setSelectedPlan]   = useState(null);
  const [availableCredits, setAvailableCredits] = useState(0);
  const [phone,          setPhone]          = useState('');
  const [phoneErr,       setPhoneErr]       = useState('');

  useEffect(() => {
    dealerAPI.getListingCreditPlans()
      .then(res => {
        setPlans(res.data.plans || []);
        setAvailableCredits(res.data.available_credits || 0);
        if (res.data.plans?.length) setSelectedPlan(res.data.plans[1]?.plan || res.data.plans[0].plan);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const { step, errorMsg, pay, reset } = useDealerPlanPayment({
    onInitiate: useCallback(async () => {
      const res = await dealerAPI.initiateListingCreditPurchase(selectedPlan);
      return res.data;
    }, [selectedPlan]),
    onConfirm: useCallback(async (data) => {
      await dealerAPI.confirmListingCreditPurchase(data);
    }, []),
    onSuccess: () => {
      onSuccess?.();
      setTimeout(onClose, 2000);
    },
    description: `Listing Credits – ${selectedPlan}`,
  });

  const handlePay = () => {
    if (!selectedPlan) return;
    if (!/^[6-9]\d{9}$/.test(phone.trim())) {
      setPhoneErr('Enter valid 10-digit number'); return;
    }
    setPhoneErr('');
    pay(phone.trim());
  };

  return (
    <Overlay onClose={step !== 'processing' ? onClose : undefined}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes popIn{from{opacity:0;transform:scale(0.88)}to{opacity:1;transform:scale(1)}}`}</style>

      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg,#003566,#001d3d)', padding: '20px 24px', borderBottom: '3px solid #FFD60A', borderRadius: '16px 16px 0 0' }}>
        <h2 style={{ margin: 0, color: '#FFD60A', fontSize: 18, fontWeight: 800 }}>🏷️ Buy Listing Credits</h2>
        <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.65)', fontSize: 13 }}>
          Available credits: <strong style={{ color: '#FFD60A' }}>{availableCredits}</strong>
        </p>
      </div>

      <div style={{ padding: 24 }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <div style={{ width: 36, height: 36, border: '4px solid #e0e0e0', borderTopColor: '#003566', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
          </div>
        )}

        {!loading && step === 'idle' && (
          <>
            {/* Plans */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {plans.map(p => (
                <div
                  key={p.plan}
                  onClick={() => setSelectedPlan(p.plan)}
                  style={{
                    border: `2px solid ${selectedPlan === p.plan ? '#003566' : '#e5e7eb'}`,
                    borderRadius: 12, padding: '14px 16px', cursor: 'pointer',
                    background: selectedPlan === p.plan ? '#f0f4ff' : '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    transition: 'all 0.15s', position: 'relative',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#003566' }}>{p.label}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                      {p.credits} car listing{p.credits > 1 ? 's' : ''} approved
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: '#003566' }}>{fmt(p.amount)}</div>
                    {p.credits > 1 && (
                      <div style={{ fontSize: 11, color: '#059669' }}>
                        {fmt(Math.round(p.amount / p.credits))}/listing
                      </div>
                    )}
                  </div>
                  {p.popular && (
                    <div style={{
                      position: 'absolute', top: -8, right: 12,
                      background: '#FFD60A', color: '#003566', fontSize: 10,
                      fontWeight: 800, padding: '2px 10px', borderRadius: 20,
                    }}>POPULAR</div>
                  )}
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%', flexShrink: 0, marginLeft: 12,
                    border: `2px solid ${selectedPlan === p.plan ? '#003566' : '#d1d5db'}`,
                    background: selectedPlan === p.plan ? '#003566' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {selectedPlan === p.plan && <span style={{ color: '#fff', fontSize: 12 }}>✓</span>}
                  </div>
                </div>
              ))}
            </div>

            {/* Phone */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#003566', display: 'block', marginBottom: 6 }}>
                Mobile Number (for receipt)
              </label>
              <div style={{ display: 'flex', alignItems: 'center', border: `2px solid ${phoneErr ? '#dc3545' : '#e5e7eb'}`, borderRadius: 10, overflow: 'hidden' }}>
                <span style={{ padding: '0 12px', fontWeight: 700, color: '#003566', borderRight: '1px solid #e5e7eb', background: '#f3f6fb', height: 44, display: 'flex', alignItems: 'center' }}>+91</span>
                <input
                  type="tel" maxLength={10} placeholder="98XXXXXXXX" value={phone}
                  onChange={e => setPhone(e.target.value.replace(/\D/g, ''))}
                  style={{ flex: 1, padding: '11px 12px', border: 'none', outline: 'none', fontSize: 14 }}
                />
              </div>
              {phoneErr && <p style={{ color: '#dc3545', fontSize: 12, margin: '4px 0 0' }}>{phoneErr}</p>}
            </div>

            {errorMsg && (
              <div style={{ background: '#fff1f1', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 12 }}>
                {errorMsg}
              </div>
            )}

            <button
              onClick={handlePay}
              disabled={!selectedPlan}
              style={{
                width: '100%', padding: 14, borderRadius: 10,
                background: 'linear-gradient(135deg,#003566,#001d3d)',
                border: '2px solid #FFD60A', color: '#FFD60A',
                fontWeight: 800, fontSize: 15, cursor: 'pointer',
              }}
            >
              🔒 Pay {selectedPlan ? fmt(plans.find(p => p.plan === selectedPlan)?.amount || 0) : ''} →
            </button>
          </>
        )}

        {step === 'processing' && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ width: 48, height: 48, border: '5px solid #e0e0e0', borderTopColor: '#003566', borderRadius: '50%', animation: 'spin 0.9s linear infinite', margin: '0 auto 16px' }} />
            <p style={{ fontWeight: 700, color: '#003566' }}>Opening payment gateway…</p>
          </div>
        )}

        {step === 'success' && (
          <div style={{ textAlign: 'center', padding: '40px 24px' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg,#28a745,#20c997)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, margin: '0 auto 16px', animation: 'popIn 0.4s ease' }}>✓</div>
            <h3 style={{ color: '#003566', margin: '0 0 8px' }}>Credits Added! 🎉</h3>
            <p style={{ color: '#666', fontSize: 14 }}>Your listing credits have been added to your account.</p>
          </div>
        )}

        {step === 'error' && (
          <div style={{ textAlign: 'center', padding: '32px 20px' }}>
            <p style={{ fontSize: 40, margin: '0 0 12px' }}>❌</p>
            <p style={{ fontWeight: 700, color: '#dc3545', margin: '0 0 8px' }}>Payment Failed</p>
            <p style={{ color: '#666', margin: '0 0 20px', fontSize: 14 }}>{errorMsg}</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button onClick={reset} style={{ padding: '10px 20px', background: '#003566', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Try Again</button>
              <button onClick={onClose} style={{ padding: '10px 20px', background: '#f3f4f6', border: '1px solid #ddd', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        )}
      </div>
    </Overlay>
  );
}

const Overlay = ({ children, onClose }) => (
  <div
    style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    onClick={onClose}
  >
    <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 440, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.4)' }}>
      {children}
    </div>
  </div>
);